import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, Stack, CircularProgress, Alert, Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { httpClient } from '../services/http-client';
import { chartTooltipProps } from './charts/chartTooltip';
import { formatCurrency, formatShares } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

export interface SymbolLedgerEntry {
  trade_id: string;
  date: string;
  action: string;
  shares: string;
  price: string;
  fees: string;
  currency: string;
  shares_held: string;
  total_acb: string;
  acb_per_share: string;
  proceeds?: string;
  capital_gain?: string;
  denied_loss?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  symbol: string;
}

const ACQUIRE = ['BUY', 'REINVEST', 'TRANSFER_IN'];

/**
 * The full purchase and disposition history for one holding.
 *
 * A cost basis with no history behind it cannot be checked against a statement,
 * reconciled, or defended to CRA. Showing the pooled ACB after EVERY trade is
 * also the only way the averaging rule becomes legible: a sale does not remove
 * the shares it sold at the price they were bought, it removes them at the
 * running average, and watching that number move is what makes the final
 * figure believable rather than asserted.
 */
export const SymbolLedgerDialog: React.FC<Props> = ({ open, onClose, accountId, symbol }) => {
  const [rows, setRows] = useState<SymbolLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseCurrency = useBaseCurrency();

  useEffect(() => {
    if (!open || !accountId || !symbol) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    httpClient
      .get<SymbolLedgerEntry[]>(
        `/investments/accounts/${accountId}/symbol/${encodeURIComponent(symbol)}/ledger`)
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res) ? res : []);
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load history'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, accountId, symbol]);

  const last = rows[rows.length - 1];
  const totalGain = rows.reduce(
    (sum, r) => sum + (r.capital_gain ? parseFloat(r.capital_gain) : 0), 0);
  const totalDenied = rows.reduce(
    (sum, r) => sum + (r.denied_loss ? parseFloat(r.denied_loss) : 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="h6" component="span" fontWeight={700}>{symbol}</Typography>
          <Typography variant="body2" color="text.secondary">
            purchase &amp; disposition history
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="info">
            No trade history for this holding. The position came from your
            institution&apos;s snapshot, so there are no purchase dates to show.
            Import an activity export to get the full history.
          </Alert>
        )}

        {!loading && rows.length > 0 && (
          <>
            <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">Shares held</Typography>
                <Typography variant="h6">{formatShares(last.shares_held)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Adjusted cost base</Typography>
                <Typography variant="h6">
                  {formatCurrency(last.total_acb, last.currency, { base: baseCurrency })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">ACB per share</Typography>
                <Typography variant="h6">
                  {formatCurrency(last.acb_per_share, last.currency, { base: baseCurrency })}
                </Typography>
              </Box>
              {totalGain !== 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Realised gain to date</Typography>
                  <Typography variant="h6" sx={{ color: totalGain >= 0 ? 'var(--inflow-text)' : 'var(--danger-text)' }}>
                    {formatCurrency(totalGain, last.currency, { base: baseCurrency })}
                  </Typography>
                </Box>
              )}
            </Stack>

            {totalDenied > 0 && (
              // Surfaced rather than folded into the gain: a denied loss is not
              // lost, it is added to the ACB of the repurchased shares, and a
              // user who cannot see it happen has no way to check the return.
              <Alert severity="warning" sx={{ mb: 2 }}>
                {formatCurrency(totalDenied, last.currency, { base: baseCurrency })} of loss was
                denied as superficial — repurchased within 30 days. That amount is
                added to the cost base of the shares still held rather than claimed.
              </Alert>
            )}

            {/*
              ACB per share against the position size, over time.

              Chosen deliberately over a price chart. A price chart is available
              from any broker and says nothing about this household; what cannot
              be seen anywhere else is how the POOLED cost base moved as
              purchases pulled it around and sales left it untouched. Watching
              the line stay flat through a disposition is what makes the
              averaging rule click -- and it is the number a capital gain is
              computed from.
            */}
            <Box sx={{ height: 240, mb: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={rows.map((r) => ({
                    date: r.date.slice(0, 10),
                    acb: parseFloat(r.acb_per_share),
                    price: parseFloat(r.price),
                    held: parseFloat(r.shares_held),
                  }))}
                  margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="money" tick={{ fontSize: 11 }} width={64}
                    tickFormatter={(v) => formatCurrency(v, last.currency, { base: baseCurrency, compact: true })} />
                  <YAxis yAxisId="shares" orientation="right" tick={{ fontSize: 11 }} width={56} />
                  <RTooltip
                    {...chartTooltipProps}
                    formatter={(v: any, name: string) =>
                      name === 'Shares held'
                        ? formatShares(v)
                        : formatCurrency(v, last.currency, { base: baseCurrency })}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {/* Reason: animated series render blank on first mount under
                      React 18 StrictMode — same workaround as PositionMix. */}
                  <Bar yAxisId="shares" dataKey="held" name="Shares held"
                    fill="var(--border-soft)" barSize={18} isAnimationActive={false} />
                  <Line yAxisId="money" type="stepAfter" dataKey="acb" name="ACB / share"
                    stroke="var(--inflow-text)" strokeWidth={2} dot isAnimationActive={false} />
                  {/* Trade price alongside, so a purchase above or below the
                      running average explains which way the line moved. */}
                  <Line yAxisId="money" type="monotone" dataKey="price" name="Trade price"
                    stroke="var(--text-secondary, #888)" strokeDasharray="4 3" strokeWidth={1} dot={false}
                    isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Divider sx={{ mb: 1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell align="right">Shares</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Fees</TableCell>
                  <TableCell align="right">Held after</TableCell>
                  <TableCell align="right">ACB / share</TableCell>
                  <TableCell align="right">Gain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const acquiring = ACQUIRE.includes(r.action);
                  const gain = r.capital_gain ? parseFloat(r.capital_gain) : null;
                  return (
                    <TableRow key={r.trade_id} hover>
                      <TableCell>{r.date.slice(0, 10)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.action.replace('_', ' ')}
                          color={acquiring ? 'primary' : 'default'}
                          variant={acquiring ? 'filled' : 'outlined'}
                          sx={{ height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="right">{formatShares(r.shares)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(r.price, r.currency, { base: baseCurrency })}
                      </TableCell>
                      <TableCell align="right">
                        {Number(r.fees) ? formatCurrency(r.fees, r.currency, { base: baseCurrency }) : '—'}
                      </TableCell>
                      <TableCell align="right">{formatShares(r.shares_held)}</TableCell>
                      {/* The column that makes pooled averaging visible. */}
                      <TableCell align="right" sx={{ fontWeight: 650 }}>
                        {formatCurrency(r.acb_per_share, r.currency, { base: baseCurrency })}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: gain == null ? 'inherit'
                          : gain >= 0 ? 'var(--inflow-text)' : 'var(--danger-text)',
                      }}>
                        {gain == null ? '—' : formatCurrency(gain, r.currency, { base: baseCurrency })}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Cost base is pooled across identical property, as CRA requires — a sale
              removes shares at the running average, not at the price they were bought.
            </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
